<template>
  <div class="quotes-detail"
       v-loading="isLoading">

    <app-alert :description="$t('QUOTES_DETAIL.ERRORS.GET.DESCRIPTION')"
               :label="$t('QUOTES_DETAIL.ERRORS.GET.LABEL')"
               :retryable="true"
               @retry="getQuote"
               type="error"
               v-if="isInError">
    </app-alert>

    <div v-else-if="Object.keys(quote).length > 0">
      <el-page-header :content="quoteTitle"
                      @back="goBack">
      </el-page-header>

      <el-tabs class="content"
               stretch
               v-model="activeTab">
        <el-tab-pane :label="$t('QUOTES_DETAIL.TITLE')"
                     name="tab-detail">

          <el-row :gutter="20"
                  type="flex">
            <el-col :lg="12"
                    :md="12">
              <app-alert :title="`${hazardousTitle}`"
                         class="hazardous-detail"
                         type="warning"
                         v-if="quote.hazardous">
              </app-alert>

              <div class="global-detail">
                <div class="line">
                  <span></span>
                  <app-state-alert :value="quote.state"></app-state-alert>
                </div>
                <div class="line">
                  <span>{{ $t('QUOTES_DETAIL.EXPECTED_AMOUNT.TITLE') }}</span>
                  <strong>
                    {{ quote.expectedAmount | currency({ currency: quote.currency,
                    minimumFractionDigits:
                    0 }) }}
                  </strong>
                </div>
                <div class="line">
                  <span>{{ $t('QUOTES_DETAIL.EQUIPMENT_TYPE.TITLE') }}</span>
                  <strong>{{ $t(`QUOTES_DETAIL.EQUIPMENT_TYPE.${quote.equipmentType}`) }}</strong>
                </div>
                <div class="line">
                  <span>{{ $t('QUOTES_DETAIL.PACKAGING_TYPE.TITLE') }}</span>
                  <strong>{{ $t(`QUOTES_DETAIL.PACKAGING_TYPE.${quote.packagingType}`) }}</strong>
                </div>
                <div class="line">
                  <span>{{ $t('QUOTES_DETAIL.WEIGHT.TITLE') }}</span>
                  <strong>{{ quote.weight | number('TON', 'LB') }}</strong>
                </div>

                <p>{{ quote.goodsDescription }}</p>
              </div>
            </el-col>

            <el-col :lg="12"
                    :md="12"
                    class="hidden-sm-and-down">
              <quotes-detail-location-and-date :value="quote.pickup"
                                               class="pickup-detail">
                {{ $t('QUOTES_DETAIL.PICKUP.TITLE') }}
              </quotes-detail-location-and-date>

              <quotes-detail-location-and-date :value="quote.delivery"
                                               class="delivery-detail">
                {{ $t('QUOTES_DETAIL.DELIVERY.TITLE') }}
              </quotes-detail-location-and-date>
            </el-col>
          </el-row>
          <el-row type="flex">
            <el-col class=hidden-md-and-up>
              <quotes-detail-location-and-date :value="quote.pickup"
                                               class="pickup-detail">
                {{ $t('QUOTES_DETAIL.PICKUP.TITLE') }}
              </quotes-detail-location-and-date>

              <quotes-detail-location-and-date :value="quote.delivery"
                                               class="delivery-detail">
                {{ $t('QUOTES_DETAIL.DELIVERY.TITLE') }}
              </quotes-detail-location-and-date>
            </el-col>
          </el-row>
        </el-tab-pane>
        <el-tab-pane name="tab-rate-response">
          <span slot="label">
            {{ $t('QUOTES_DETAIL.RATE_RESPONSE.TITLE') }}
            <el-badge :hidden="quote.numberOfResponses === 0"
                      :value="quote.numberOfResponses"
                      class=" mark"
                      type="primary">
            </el-badge>
          </span>

          <div :infinite-scroll-disabled="isLoading"
               v-infinite-scroll="getNextResponses">
            <quotes-detail-response-resume :key="`response_${response.stateId}`"
                                           :value="response"
                                           @click.native="confirmResponse(response)"
                                           class="response"
                                           v-for="response in responses">
            </quotes-detail-response-resume>
          </div>

          <el-dialog :fullscreen="true"
                     :title="$t('QUOTES_DETAIL.PROMPT')"
                     :visible.sync="isDialogOpened">
            <span class="dialog-footer" slot="footer">
              <el-button @click="isDialogOpened = false">{{ $t('ACTIONS.CANCEL') }}</el-button>
              <el-button @click="selectResponse"
                         type="primary">{{ $t('ACTIONS.ACCEPT') }}</el-button>
            </span>
          </el-dialog>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
</template>

<script>
import AppAlert from '@/components/alert/Alert'
import AppStateAlert from '@/components/state-alert/StateAlert'
import quotesService from '@/services/quotesService'
import QuotesDetailLocationAndDate from './components/QuotesDetailLocationAndDate'
import QuotesDetailResponseResume from './components/QuotesDetailResponseResume'

export default {
  name: 'QuotesDetail',
  components: { AppStateAlert, AppAlert, QuotesDetailResponseResume, QuotesDetailLocationAndDate },
  data () {
    return {
      isLoading: false,
      isInError: false,
      isDialogOpened: false,
      activeTab: 'tab-detail',
      quote: {},
      selectedResponse: {},
      paginationResponses: {},
      responses: []
    }
  },
  computed: {
    quoteTitle () {
      const quoteTitles = []

      if (this.quote.reference) {
        quoteTitles.push(this.quote.reference)
      }

      if (this.quote.name) {
        quoteTitles.push(this.quote.name)
      }

      return quoteTitles.join(' - ')
    },
    hazardousTitle () {
      return `${this.quote.hazardousInfo.tunnelCode} ${this.quote.hazardousInfo.additionalInfo}`
    }
  },
  created () {
    this.getQuote()
    this.getResponses()
  },
  methods: {
    async getQuote () {
      this.isLoading = true
      this.isInError = false
      const id = this.$route.params.id

      try {
        this.quote = await quotesService.findById(id)
        this.isLoading = false
      } catch (error) {
        this.isInError = true
        this.isLoading = false
        throw error
      }
    },
    async getResponses (page = 1) {
      this.loading = this.$loading({ lock: true })
      this.isLoading = true
      const id = this.$route.params.id

      try {
        const dataResponses = await quotesService.findAllResponsesByQuoteId(id, page)
        this.paginationResponses = dataResponses.pagination

        this.responses = dataResponses.responses
        this.responses.sort((r1, r2) => r1.bidAmount && r2.bidAmount ? r1.bidAmount - r2.bidAmount : r1.bidAmount)
      } catch (error) {
        console.error(error)
        this.isInError = true
      }

      this.loading.close()
      this.isLoading = false
    },
    getNextResponses () {
      if (this.paginationResponses.currentPage < this.paginationResponses.totalPages) {
        this.getResponses(this.paginationResponses.currentPage + 1)
      }
    },
    confirmResponse (response) {
      if (response.state === 'SUBMITTED') {
        this.isDialogOpened = true
        this.selectedResponse = response
      }
    },
    selectResponse () {
      quotesService.selectResponse(this.$route.params.id, this.selectedResponse.id)
      this.isDialogOpened = false
    },
    goBack () {
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.quotes-detail {
  min-height: 100%;

  .content {
    margin-top: 1rem;

    .hazardous-detail {
      margin-bottom: 1rem;
    }

    .global-detail {
      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .line + .line {
        margin-top: 0.5rem;
      }
    }

    .delivery-detail {
      margin-top: 0.5rem;
    }

    .response + .response {
      margin-top: 0.5rem;
    }
  }
}
</style>
